import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Settings, Check, Trash2, Volume2, VolumeX } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const NotificationCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [preferences, setPreferences] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    fetchData();
    checkSubscription();
  }, []);

  const fetchData = async () => {
    try {
      const [notifRes, prefRes] = await Promise.all([
        api.get('/push/history?limit=50'),
        api.get('/push/preferences')
      ]);
      setNotifications(notifRes.data);
      setPreferences(prefRes.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkSubscription = async () => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (error) {
        console.error('Error checking subscription:', error);
      }
    }
  };

  const subscribeToPush = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      toast.error('Push notifications not supported in this browser');
      return;
    }

    try {
      // Get VAPID key
      const keyRes = await api.get('/push/vapid-key');
      if (!keyRes.data.public_key) {
        toast.error('Push notifications not configured');
        return;
      }

      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Notification permission denied');
        return;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyRes.data.public_key)
      });

      // Send subscription to backend
      await api.post('/push/subscribe', {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))))
        }
      });

      setIsSubscribed(true);
      toast.success('Push notifications enabled!');
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Failed to enable notifications');
    }
  };

  const unsubscribeFromPush = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        await api.post('/push/unsubscribe', { endpoint: subscription.endpoint });
      }

      setIsSubscribed(false);
      toast.success('Push notifications disabled');
    } catch (error) {
      toast.error('Failed to disable notifications');
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/push/mark-read/${notificationId}`);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/push/mark-all-read');
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const updatePreferences = async (newPrefs) => {
    try {
      await api.put('/push/preferences', newPrefs);
      setPreferences(newPrefs);
      toast.success('Preferences updated');
    } catch (error) {
      toast.error('Failed to update preferences');
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="notification-center">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Bell className="w-8 h-8 text-primary" />
              Notifications
              {unreadCount > 0 && (
                <span className="px-2 py-1 bg-primary text-primary-foreground rounded-full text-sm">
                  {unreadCount} new
                </span>
              )}
            </h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 hover:bg-white/10 rounded-lg"
              title="Settings"
            >
              <Settings className="w-5 h-5" />
            </button>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="px-3 py-2 bg-white/5 rounded-lg text-sm hover:bg-white/10"
              >
                Mark all read
              </button>
            )}
          </div>
        </div>

        {/* Push Status */}
        <div className="glass-card p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isSubscribed ? (
              <Volume2 className="w-6 h-6 text-green-500" />
            ) : (
              <VolumeX className="w-6 h-6 text-muted-foreground" />
            )}
            <div>
              <p className="font-medium">
                Push Notifications: {isSubscribed ? 'Enabled' : 'Disabled'}
              </p>
              <p className="text-sm text-muted-foreground">
                {isSubscribed 
                  ? "You'll receive real-time updates" 
                  : "Enable to get instant notifications"}
              </p>
            </div>
          </div>
          <button
            onClick={isSubscribed ? unsubscribeFromPush : subscribeToPush}
            className={`px-4 py-2 rounded-lg ${
              isSubscribed 
                ? 'bg-red-500/20 text-red-500 hover:bg-red-500/30' 
                : 'bg-green-500/20 text-green-500 hover:bg-green-500/30'
            }`}
          >
            {isSubscribed ? 'Disable' : 'Enable'}
          </button>
        </div>

        {/* Settings Panel */}
        {showSettings && preferences && (
          <div className="glass-card p-6 mb-6">
            <h3 className="font-semibold mb-4">Notification Preferences</h3>
            <div className="space-y-3">
              {[
                { key: 'orders', label: 'Order Updates', desc: 'Order confirmations, shipping, delivery' },
                { key: 'wallet', label: 'Wallet Updates', desc: 'Credits, debits, balance changes' },
                { key: 'commissions', label: 'Commission Alerts', desc: 'New commissions earned' },
                { key: 'promotions', label: 'Promotions', desc: 'Offers, discounts, announcements' },
                { key: 'system', label: 'System Notifications', desc: 'Important updates, security alerts' }
              ].map(item => (
                <div key={item.key} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div>
                    <p className="font-medium">{item.label}</p>
                    <p className="text-xs text-muted-foreground">{item.desc}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences[item.key]}
                      onChange={(e) => updatePreferences({
                        ...preferences,
                        [item.key]: e.target.checked
                      })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-white/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notifications List */}
        {notifications.length > 0 ? (
          <div className="space-y-3">
            {notifications.map(notif => (
              <div
                key={notif.id}
                className={`glass-card p-4 flex items-start gap-4 ${
                  !notif.read ? 'border-l-4 border-primary' : ''
                }`}
              >
                <div className={`p-2 rounded-lg ${getNotifIconBg(notif.type)}`}>
                  {getNotifIcon(notif.type)}
                </div>
                <div className="flex-1">
                  <p className={`font-medium ${!notif.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {notif.title}
                  </p>
                  <p className="text-sm text-muted-foreground">{notif.body}</p>
                  {notif.meta?.wa_link && (
                    <a href={notif.meta.wa_link} target="_blank" rel="noopener noreferrer"
                       className="inline-block mt-2 px-3 py-1 rounded-lg bg-green-600 text-white text-xs font-semibold">
                      Open in WhatsApp
                    </a>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(notif.created_at).toLocaleString()}
                  </p>
                </div>
                {!notif.read && (
                  <button
                    onClick={() => markAsRead(notif.id)}
                    className="p-2 hover:bg-white/10 rounded-lg"
                    title="Mark as read"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <BellOff className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Notifications</h3>
            <p className="text-muted-foreground">You're all caught up!</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Helper functions for notification icons
function getNotifIcon(type) {
  const icons = {
    order: '📦',
    wallet: '💰',
    commission: '🎊',
    promotion: '🎁',
    system: '🔔'
  };
  return <span className="text-xl">{icons[type] || icons.system}</span>;
}

function getNotifIconBg(type) {
  const colors = {
    order: 'bg-blue-500/20',
    wallet: 'bg-green-500/20',
    commission: 'bg-purple-500/20',
    promotion: 'bg-pink-500/20',
    system: 'bg-gray-500/20'
  };
  return colors[type] || colors.system;
}

export default NotificationCenter;
